import React from 'react';
// при переезде на Vite стала падать ошибка https://github.com/TanStack/table/issues/3297, фикснулось этой библиотекой
import 'regenerator-runtime/runtime.js';
import '@vlabs/uikit/assets/css/uikit.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@vlabs/api';
import { getSelf, getRole } from '@vlabs/api/artemy/client';
import { getDarkLogoUrl, getLogoUrl } from '@vlabs/monokit/shared/lib/branding';
import { PermissionProvider, adaptArtemyPermissions, PermissionRegistry } from '@vlabs/permissions';
import { setSimilarityThresholds } from '@vlabs/shared/config/similarityThresholds';
import { setRoutes } from '@vlabs/shared/navigation/routes';
import { registerErrorHandlers } from '@vlabs/shared/utils/error-handler';
import { createBrowserHistory } from 'history';
import { createRoot } from 'react-dom/client';
import { Router } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import App from './app/PointApp';
import { routes } from './app/routes';
import { i18nReady } from './app/translations/i18n';

import './app/styles/index.sass';
import './app/styles/toasts.sass';
import './app/styles/variables.sass';

PermissionRegistry.register({
  appName: 'point3',
  resources: {
    card: { actions: ['create', 'read', 'update', 'delete'] },
    face: { actions: ['create', 'read', 'update', 'delete'] },
    list: { actions: ['create', 'read', 'update', 'delete'] },
    roles: { actions: ['create', 'read', 'update', 'delete'] },
    users: { actions: ['create', 'read', 'update'] },
    handler: { actions: ['create', 'read', 'update', 'delete'] },
    session: { actions: ['read', 'update', 'delete'] },
    event_fs: { actions: ['read', 'delete'] },
    group: { actions: ['create', 'read', 'update', 'delete'] },
    stream: { actions: ['create', 'read', 'update', 'delete'] },
    alarm: { actions: ['read', 'update'] },
    task: { actions: ['create', 'read', 'delete'] },
    departments: { actions: ['create', 'read', 'update', 'delete'] },
    audit_log: { actions: ['read', 'delete'] },
    audit_config: { actions: ['create', 'read', 'update', 'delete'] },
    card_document: { actions: ['create', 'read', 'update', 'delete'] },
    permission_tags: { actions: ['create', 'read', 'delete'] },
    card_custom_field: { actions: ['create', 'read', 'update', 'delete'] },
    notification: { actions: ['create', 'read', 'update', 'delete'] },
    event_send_rule: { actions: ['create', 'read', 'update', 'delete'] },
  },
  pages: {
    operator_panel: { label: 'Operator Panel' },
    search: { label: 'Search' },
    events_fs_archive: { label: 'Events Archive' },
    event_fs_details: { label: 'Event FS Details' },
    videowall: { label: 'Video Wall' },
    cards: { label: 'Cards' },
    cards_details: { label: 'Card Details' },
    face_details: { label: 'Face Details' },
    lists: { label: 'Lists' },
    lists_details: { label: 'List Details' },
    streams_fs: { label: 'Streams' },
    handlers: { label: 'Handlers' },
    users: { label: 'Users' },
    users_details: { label: 'User Details' },
    roles: { label: 'Roles' },
    roles_details: { label: 'Role Details' },
    departments: { label: 'Departments' },
    permission_tags: { label: 'Permission Tags' },
    permissions_tags_details: { label: 'Permission Tag Details' },
    statistics_fs: { label: 'Statistics' },
    monitoring: { label: 'Monitoring' },
    licenses: { label: 'Licenses' },
    clementine_accounts: { label: 'Clementine Accounts' },
  },
  resourceAliases: {},
});

const history = createBrowserHistory();

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);

setRoutes(routes);

if (import.meta.env.MODE === 'production') {
  registerErrorHandlers();
}

queryClient.prefetchQuery({ queryKey: ['self'], queryFn: getSelf });

{
  const configEl = document.querySelector('link[rel="config"]');
  if (configEl?.getAttribute('href')) {
    const configUrl = configEl.getAttribute('href');
    if (configUrl) {
      (async () => {
        const resp = await fetch(configUrl);
        const config = await resp.json();
        setSimilarityThresholds(config.similarityThresholds);

        const logoUrl = getLogoUrl(import.meta.env.VITE_LOGO);
        const darkLogoUrl = getDarkLogoUrl(import.meta.env.VITE_LOGO_DARK, logoUrl);

        if (logoUrl) {
          document.documentElement.style.setProperty('--logo-url', `url(${logoUrl})`);
          document.documentElement.style.setProperty('--dark-logo-url', `url(${darkLogoUrl ?? logoUrl})`);
        }

        if (config.logoUrl) {
          document.documentElement.style.setProperty('--logo-url', `url(${config.logoUrl})`);
          document.documentElement.style.setProperty('--dark-logo-url', `url(${config?.darkLogoUrl ?? config.logoUrl})`);
        }
      })();
    }
  }
}

// Ждём загрузки переводов (динамический import локалей по VITE_LOCALES) перед первым рендером.
i18nReady.then(() => {
  root.render(
    <React.StrictMode>
      <Router history={history}>
        <QueryClientProvider client={queryClient}>
          <PermissionProvider
            appName="point3"
            fetchPermissions={async () => {
              const self = await getSelf();
              const role = await getRole(self.role_id);
              if (self.is_system) {
                const permissions = adaptArtemyPermissions(role.permissions);
                permissions.pages = ['*'];
                return permissions;
              }
              const permissions = adaptArtemyPermissions(role.permissions);
              // ponytail: temporary hardcoded page for accounts until clementine accounts are properly handled
              permissions.pages = [...(permissions.pages ?? []), 'clementine_accounts'];
              return permissions;
            }}
            refetchInterval={5 * 60 * 1000}
            refetchOnWindowFocus
          >
            <App />
            <ToastContainer
              hideProgressBar
              position={toast.POSITION.TOP_LEFT}
            />
          </PermissionProvider>
        </QueryClientProvider>
      </Router>
    </React.StrictMode>,
  );
});

// expose queryClient when run in Cypress
// FIXME: use only in test environment
if (window.Cypress) {
  window.cprs = {
    queryClient,
    history,
  };
}
